import React from "react";
import { C } from "../theme";
import Button from "./Button";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function toDate(trip) {
  return trip?.startAt?.toDate ? trip.startAt.toDate() : null;
}

function countdownLabel(date, nowMs = Date.now()) {
  if (!date) return "";
  const ms = date.getTime() - nowMs;
  if (ms <= 0) return "now";
  const totalSec = Math.floor(ms / 1000);
  const d = Math.floor(totalSec / 86400);
  const h = Math.floor((totalSec % 86400) / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

// Calendar icon (stroke style, matches the app's other line icons).
function CalendarIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ display: "block", flexShrink: 0 }}>
      <rect x="3" y="4.5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
      <path d="M3 9h18M8 3v3M16 3v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M12 12.5v4M10 14.5h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function timeLabel(date) {
  if (!date) return "";
  return new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(date);
}

const REPEAT_LABEL = { daily: "↻ Daily", weekly: "↻ Weekly" };

export default function PlannedTripsModal({
  open,
  onClose,
  trips = [],
  speciesCatalog = [],
  onAdd,
  onEdit,
  onDelete,
}) {
  const speciesLabel = React.useCallback(
    (id) => speciesCatalog.find((s) => s.id === id)?.label || id,
    [speciesCatalog]
  );

  // Tick every second while open so the countdown pills stay live.
  const [nowMs, setNowMs] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!open) return undefined;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [open]);

  if (!open) return null;

  // Show upcoming trips first, then any elapsed one-off trips — so a passed
  // trip stays visible and deletable rather than lingering as an orphan the
  // count includes but the list hides.
  const now = Date.now();
  const withMs = trips.map((t) => ({ t, ms: toDate(t)?.getTime() || 0 }));
  const upcomingList = withMs
    .filter((x) => x.t.repeat !== "once" || x.ms >= now)
    .sort((a, b) => a.ms - b.ms);
  const pastList = withMs
    .filter((x) => x.t.repeat === "once" && x.ms < now)
    .sort((a, b) => b.ms - a.ms);
  const display = [...upcomingList, ...pastList].map((x) => x.t);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        top: 0,
        bottom: 0,
        left: "50%",
        transform: "translateX(-50%)",
        width: "100%",
        maxWidth: 375,
        background: "rgba(4,8,15,0.72)",
        zIndex: 1200,
        display: "flex",
        flexDirection: "column",
        backdropFilter: "blur(4px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          marginTop: "auto",
          background: C.deepWater,
          borderTop: `1px solid ${C.border}`,
          borderTopLeftRadius: 18,
          borderTopRightRadius: 18,
          maxHeight: "92%",
          display: "flex",
          flexDirection: "column",
          boxShadow: C.menuShadow,
        }}
      >
        <div style={{ padding: "16px 18px 10px", display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 10, letterSpacing: 3, color: C.teal, textTransform: "uppercase" }}>Upcoming</div>
            <div style={{ fontFamily: "Georgia,serif", fontWeight: 700, fontSize: 20, color: C.white }}>Planned Trips</div>
          </div>
          <button onClick={onClose} type="button" aria-label="Close" style={{ background: "none", border: "none", color: C.muted, fontSize: 22, cursor: "pointer" }}>
            ✕
          </button>
        </div>

        <div style={{ overflowY: "auto", padding: "4px 18px 18px" }}>
          {display.length === 0 ? (
            <div style={{ textAlign: "center", padding: "28px 8px", color: C.muted }}>
              <div style={{ marginBottom: 8, color: C.seafoam }}><CalendarIcon size={34} /></div>
              <div style={{ fontSize: 13, lineHeight: 1.5 }}>No trips planned yet. Schedule one and we'll remind you before it starts.</div>
            </div>
          ) : (
            display.map((trip) => {
              const d = toDate(trip);
              const species = (trip.targetSpeciesIds || []).slice(0, 4);
              return (
                <div key={trip.id} style={{ display: "flex", gap: 12, marginBottom: 10 }}>
                  <div style={{ width: 54, flexShrink: 0, textAlign: "center", background: C.midnight, border: `1px solid ${C.border}`, borderRadius: 10, padding: "8px 0", height: "fit-content" }}>
                    <div style={{ fontSize: 9, letterSpacing: 1, color: C.gold, textTransform: "uppercase" }}>{d ? MONTHS[d.getMonth()] : "—"}</div>
                    <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1, marginTop: 2, color: C.white }}>{d ? d.getDate() : "–"}</div>
                    <div style={{ fontSize: 9, color: C.muted, marginTop: 3 }}>{d ? DOW[d.getDay()] : ""}</div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0, background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 12, padding: 12, position: "relative", overflow: "hidden" }}>
                    <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, background: C.seafoam }} />
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: C.white, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {timeLabel(d)}{trip.name ? ` · ${trip.name}` : ""}
                      </div>
                      <span style={{ flexShrink: 0, fontSize: 10, fontWeight: 700, color: C.gold, border: `1px solid ${C.gold}80`, borderRadius: 999, padding: "2px 7px" }}>
                        {countdownLabel(d, nowMs)}
                      </span>
                    </div>
                    {trip.location?.label && (
                      <div style={{ fontSize: 12, color: C.muted, margin: "4px 0 8px" }}>{trip.location.label}</div>
                    )}
                    {(species.length > 0 || REPEAT_LABEL[trip.repeat]) && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                        {species.map((id) => (
                          <span key={id} style={{ fontSize: 10, color: C.seafoam, border: `1px solid ${C.seafoam}80`, borderRadius: 999, padding: "2px 7px" }}>
                            {speciesLabel(id)}
                          </span>
                        ))}
                        {REPEAT_LABEL[trip.repeat] && (
                          <span style={{ fontSize: 10, color: C.gold, border: `1px solid ${C.gold}80`, borderRadius: 999, padding: "2px 7px" }}>
                            {REPEAT_LABEL[trip.repeat]}
                          </span>
                        )}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: C.gold, marginTop: 8, display: "flex", alignItems: "center", gap: 6 }}>🔔 Notifies when it starts</div>
                    <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                      <Button variant="ghost" size="sm" onClick={() => onEdit?.(trip)} style={{ flex: 1 }}>
                        Edit
                      </Button>
                      <button
                        onClick={() => onDelete?.(trip)}
                        type="button"
                        aria-label="Delete trip"
                        style={{ border: `1px solid ${C.red}66`, background: "transparent", color: C.red, borderRadius: 10, padding: "0 14px", fontSize: 14, cursor: "pointer" }}
                      >
                        🗑
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}

          <div style={{ height: 8 }} />
          <Button variant="primary" fullWidth onClick={onAdd}>
            <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              <CalendarIcon size={16} />
              Schedule a Trip
            </span>
          </Button>
        </div>
      </div>
    </div>
  );
}
